import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import GooglePlacesAutocomplete from '../../components/forms/GooglePlacesAutocomplete';
import ManualVenueForm from '../../components/admin/ManualVenueForm';
import EventImageUpload from '../../components/admin/EventImageUpload';
import PricingTierEditor from '../../components/admin/PricingTierEditor';
import SearchableCategorySelect from '../../components/admin/SearchableCategorySelect';
import { DEFAULT_REFUND_POLICY } from '../../constants/eventDefaults';
import { validateEventTimeRange, getEndTimeMin } from '../../utils/eventTimeValidation';
import { resolveVenueForEvent } from '../../utils/venueHelpers';
import { adminAPI, categoriesAPI, venuesAPI, uploadAPI } from '../../lib/api';

const CreateEventPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    price: '',
    earlyBirdPrice: '',
    maxTickets: '',
    categoryId: '',
    categoryIds: [],
    venueId: '',
    organizerId: '',
    organizerName: '',
    status: 'ACTIVE',
    imageUrl: '',
    isFeatured: true,
    refundPolicy: DEFAULT_REFUND_POLICY,
    locationType: 'venue', // 'venue', 'online', 'tba'
    onlineUrl: ''
  });
  
  const [locationData, setLocationData] = useState(null);
  const [manualVenue, setManualVenue] = useState({
    name: '',
    address: '',
    city: '',
    latitude: '',
    longitude: ''
  });
  const [useManualLocation, setUseManualLocation] = useState(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [usePackages, setUsePackages] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([
    { name: '', description: '', price: '', maxQuantity: '' }
  ]);

  const isMainAdmin = useMemo(() => ['ADMIN', 'SUPER_ADMIN'].includes(currentUser?.role), [currentUser]);

  useEffect(() => {
    fetchCategories();
    fetchVenues();
    fetchOrganizers();
    fetchMapsApiKey();
  }, [isMainAdmin]);

  const fetchMapsApiKey = async () => {
    try {
      const response = await adminAPI.getSettings();
      const key = response?.data?.settings?.google_maps_api_key || '';
      setGoogleMapsApiKey(key);
      if (!key || key === 'YOUR_API_KEY') {
        setUseManualLocation(true);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setUseManualLocation(true);
    }
  };

  useEffect(() => {
    if (!isMainAdmin && currentUser?.id) {
      setFormData((prev) => ({
        ...prev,
        organizerId: currentUser.id,
        organizerName: currentUser.name || ''
      }));
    }
  }, [isMainAdmin, currentUser]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getCategories();
      setCategories(data.data?.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchVenues = async () => {
    try {
      const data = await venuesAPI.getVenues({ limit: 500 });
      setVenues(data.data?.venues || []);
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  };

  const fetchOrganizers = async () => {
    if (!isMainAdmin) {
      setOrganizers([]);
      return;
    }

    try {
      const data = await adminAPI.getUsers({ role: 'ORGANIZER', limit: 100 });
      setOrganizers(data.data?.users || []);
    } catch (err) {
      console.error('Error fetching organizers:', err);
    }
  };

  const handleImageChange = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    try {
      const data = await uploadAPI.uploadEventImage(imageFile, {
        title: formData.title,
        description: formData.description
      });
      return data.data?.imageUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      setError('Failed to upload image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const notifyErrors = (messages) => {
    Swal.fire({
      icon: 'error',
      title: 'Please check the form',
      html: `<ul class="text-left space-y-1">${messages.map(msg => `<li>${msg}</li>`).join('')}</ul>`,
      confirmButtonColor: '#ea580c'
    });
  };

  const validateForm = () => {
    const issues = [];

    if (!formData.title.trim()) issues.push('Event title is required.');
    if (!formData.description.trim()) issues.push('Event description is required.');
    if (!formData.date) issues.push('Event date is required.');
    if (!formData.categoryIds?.length && !formData.categoryId) {
      issues.push('Please choose at least one category.');
    }
    if (formData.locationType === 'venue') {
      if (useManualLocation || !googleMapsApiKey) {
        if (!manualVenue.name.trim() || !manualVenue.address.trim() || !manualVenue.city.trim()) {
          issues.push('Please enter venue name, address, and city.');
        }
      } else if (!locationData) {
        issues.push('Please select a location using the search field.');
      }
    }
    if (formData.locationType === 'online' && !formData.onlineUrl?.trim()) {
      issues.push('Please provide an event URL.');
    }
    if (!isFree && !usePackages && !formData.price) issues.push('Regular price is required.');
    if (!usePackages && !formData.maxTickets) issues.push('Max tickets is required.');
    if (usePackages) {
      const validTiers = pricingTiers.filter((t) => t.name.trim() && t.price !== '');
      if (validTiers.length === 0) issues.push('Add at least one ticket package.');
      validTiers.forEach((tier, index) => {
        if (Number(tier.price) < 0) issues.push(`Package ${index + 1}: price must be zero or more.`);
        if (tier.maxQuantity && Number(tier.maxQuantity) < 1) {
          issues.push(`Package ${index + 1}: max quantity must be at least 1.`);
        }
      });
    }
    if (isMainAdmin && !formData.organizerId) {
      issues.push('Please select an organizer account for this event.');
    }

    if (formData.date) {
      const eventDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (eventDate < today) {
        issues.push('Event date cannot be in the past.');
      }
      if (formData.endDate) {
        const endDate = new Date(formData.endDate);
        if (endDate < eventDate) {
          issues.push('End date cannot be before the start date.');
        }
      }

      const timeError = validateEventTimeRange({
        date: formData.date,
        endDate: formData.endDate,
        startTime: formData.startTime,
        endTime: formData.endTime
      });
      if (timeError) issues.push(timeError);
    }

    if (!isFree && !usePackages) {
      if (formData.price && Number(formData.price) <= 0) {
        issues.push('Regular price must be greater than zero.');
      }

      if (formData.earlyBirdPrice) {
        const early = Number(formData.earlyBirdPrice);
        const regular = Number(formData.price || 0);
        if (early <= 0) {
          issues.push('Early bird price must be greater than zero.');
        }
        if (regular && early > regular) {
          issues.push('Early bird price must not exceed the regular price.');
        }
      }
    }

    if (!isFree && usePackages) {
      const validTiers = pricingTiers.filter((t) => t.name.trim() && t.price !== '');
      validTiers.forEach((tier) => {
        if (Number(tier.price) <= 0) {
          issues.push(`Package "${tier.name.trim()}": price must be greater than zero.`);
        }
      });
    }

    if (formData.maxTickets && Number(formData.maxTickets) < 1) {
      issues.push('Max tickets must be at least 1.');
    }

    return issues;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = validateForm();
    if (errors.length > 0) {
      notifyErrors(errors);
      return;
    }

    setLoading(true);

    try {
      // Upload image if provided
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        imageUrl = await uploadImage();
        if (!imageUrl) {
          setLoading(false);
          return;
        }
      }

      let finalVenueId;
      try {
        finalVenueId = await resolveVenueForEvent({
          locationType: formData.locationType,
          onlineUrl: formData.onlineUrl,
          locationData: formData.locationType === 'venue' && !useManualLocation ? locationData : null,
          manualVenue: formData.locationType === 'venue' && (useManualLocation || !googleMapsApiKey) ? manualVenue : null
        });
      } catch (venueError) {
        console.error('Venue resolution error:', venueError);
        Swal.fire({
          icon: 'error',
          title: 'Failed to save location',
          text: venueError.message || 'Unable to save the location. Please try again.',
          confirmButtonColor: '#ea580c'
        });
        setLoading(false);
        return;
      }

      const validPackages = usePackages
        ? pricingTiers
            .filter((t) => t.name.trim() && t.price !== '')
            .map((t) => ({
              name: t.name.trim(),
              description: t.description?.trim() || '',
              price: parseFloat(t.price),
              maxQuantity: t.maxQuantity ? parseInt(t.maxQuantity, 10) : null
            }))
        : [];

      const selectedOrganizer = organizers.find((org) => org.id === formData.organizerId);

      const categoryIds = (formData.categoryIds?.length
        ? formData.categoryIds
        : (formData.categoryId ? [formData.categoryId] : [])
      ).filter(Boolean);

      const eventData = {
        ...formData,
        venueId: finalVenueId,
        imageUrl,
        categoryIds,
        categoryId: categoryIds[0] || formData.categoryId,
        organizerId: isMainAdmin
          ? (formData.organizerId || currentUser?.id)
          : currentUser?.id,
        organizerDisplayName: selectedOrganizer?.name || formData.organizerName.trim(),
        price: isFree ? '0' : (validPackages.length ? String(Math.min(...validPackages.map((p) => p.price))) : formData.price),
        ...(validPackages.length > 0 && { pricingTiers: validPackages })
      };

      if (usePackages && validPackages.length > 0) {
        const tierCapacity = validPackages.reduce(
          (sum, p) => sum + (p.maxQuantity || 0),
          0
        );
        if (!formData.maxTickets && tierCapacity === 0) {
          eventData.maxTickets = 10000;
        }
      }

      delete eventData.organizerName;
      delete eventData.locationType;
      delete eventData.onlineUrl;

      // Remove empty fields
      Object.keys(eventData).forEach(key => {
        if (eventData[key] === '' || eventData[key] === null) {
          delete eventData[key];
        }
      });

      if (eventData.price) eventData.price = parseFloat(eventData.price);
      if (eventData.earlyBirdPrice) eventData.earlyBirdPrice = parseFloat(eventData.earlyBirdPrice);
      if (eventData.maxTickets) eventData.maxTickets = parseInt(eventData.maxTickets, 10);

      const data = await adminAPI.createEvent(eventData);
      Swal.fire({
        icon: 'success',
        title: 'Event created',
        text: data.message || 'The event was created successfully.',
        confirmButtonColor: '#ea580c'
      }).then(() => navigate('/admin/events'));
    } catch (error) {
      setError('An error occurred while creating the event');
      console.error('Event creation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Unexpected error',
        text: 'An error occurred while creating the event. Please try again.',
        confirmButtonColor: '#ea580c'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col ml-0 lg:ml-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="flex-1 p-6 lg:p-8 w-full overflow-y-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/admin/events')}
              className="text-gray-600 hover:text-gray-800 mb-4 flex items-center text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Events
            </button>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Create an event with AI</h1>
            <p className="text-gray-600 text-base">
              Answer a few questions about your event and our AI creation tool will use internal data to build an event page.{' '}
              <button
                type="button"
                onClick={() => {/* TODO: Implement AI toggle */}}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                You can still create an event without AI
              </button>
            </p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-lg p-8 max-w-4xl shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Event Title Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">What's the name of your event?</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    This will be your event's title. Your title will be used to help create your event's summary, description, category, and tags - so be specific!
                  </p>
                </div>
                <div className="relative">
                  <label 
                    className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                  >
                    Event title *
                  </label>
                  <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none transition-all"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <div className="relative">
                  <label 
                    className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                  >
                    Description *
                  </label>
                  <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="4"
                      className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none resize-none transition-all"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <EventImageUpload
                  inputId="create-event-image"
                  label="Event image"
                  imagePreview={imagePreview}
                  onFileSelect={handleImageChange}
                  onRemove={() => {
                    setImageFile(null);
                    setImagePreview('');
                    setFormData({ ...formData, imageUrl: '' });
                  }}
                />
              </div>

              {/* Date and Time Section */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">When does your event start and end?</h2>
                <div className="grid grid-cols-3 gap-4">
                  {/* Date Field */}
                  <div className="relative">
                    <label 
                      className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                    >
                      Date
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none transition-all [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        required
                      />
                    </div>
                  </div>

                  {/* Start Time Field */}
                  <div className="relative">
                    <label 
                      className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                    >
                      Start time
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <div className="relative">
                        <i className="fas fa-clock absolute left-4 top-4 text-gray-400 pointer-events-none"></i>
                        <input
                          type="time"
                          value={formData.startTime}
                          onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                          className="w-full py-3 pl-12 pr-4 border-0 rounded-lg focus:outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* End Time Field */}
                  <div className="relative">
                    <label 
                      className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                    >
                      End time
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <div className="relative">
                        <i className="fas fa-clock absolute left-4 top-4 text-gray-400 pointer-events-none"></i>
                        <input
                          type="time"
                          value={formData.endTime}
                          min={getEndTimeMin({
                            date: formData.date,
                            endDate: formData.endDate,
                            startTime: formData.startTime
                          })}
                          onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                          className="w-full py-3 pl-12 pr-4 border-0 rounded-lg focus:outline-none transition-all"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional. Leave blank for “till late” (no fixed end time).
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="relative">
                    <label className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none">
                      End date (optional)
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <input
                        type="date"
                        value={formData.endDate}
                        min={formData.date || undefined}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none transition-all"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      For events that finish the next day, set an end date - then the end time can be earlier than the start time.
                    </p>
                  </div>
                </div>
                
                {/* Organizer & status */}
                {isMainAdmin && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="relative">
                      <label
                        className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                      >
                        Event owner (organizer) *
                      </label>
                      <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                        <select
                          value={formData.organizerId || ''}
                          onChange={(e) => {
                            const id = e.target.value;
                            const org = organizers.find((o) => o.id === id);
                            setFormData({
                              ...formData,
                              organizerId: id,
                              organizerName: org?.name || ''
                            });
                          }}
                          className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none bg-white transition-all appearance-none"
                          required
                        >
                          <option value="">Select organizer account…</option>
                          {organizers.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name} ({org.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Create organizer accounts under Users first, then assign them here.
                      </p>
                    </div>
                    <div className="relative">
                      <label 
                        className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                      >
                        Status
                      </label>
                      <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                        <div className="relative">
                          <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full py-3 px-4 pr-10 border-0 rounded-lg focus:outline-none appearance-none bg-white transition-all"
                          >
                            <option value="DRAFT">Draft</option>
                            <option value="ACTIVE">Active</option>
                            <option value="SOLD_OUT">Sold Out</option>
                            <option value="CANCELLED">Cancelled</option>
                          </select>
                          <i className="fas fa-chevron-down absolute right-4 top-4 text-gray-400 pointer-events-none"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Location Section */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Where is it located?</h2>
                
                {/* Location Type Buttons */}
                <div className="flex gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, locationType: 'venue' })}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg border transition-all ${
                      formData.locationType === 'venue'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    <span>Venue</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, locationType: 'online' })}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg border transition-all ${
                      formData.locationType === 'online'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <i className="fas fa-video"></i>
                    <span>Online event</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, locationType: 'tba' })}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg border transition-all ${
                      formData.locationType === 'tba'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <i className="fas fa-calendar-alt"></i>
                    <span>To be announced</span>
                  </button>
                </div>

                {/* Location Search/Select */}
                {formData.locationType === 'venue' && (
                  <div className="space-y-4" key="venue-location">
                    {googleMapsApiKey && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={useManualLocation}
                          onChange={(e) => {
                            setUseManualLocation(e.target.checked);
                            if (e.target.checked) {
                              setLocationData(null);
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Enter location manually instead of search
                      </label>
                    )}

                    {!useManualLocation && googleMapsApiKey ? (
                      <>
                        <GooglePlacesAutocomplete
                          apiKey={googleMapsApiKey}
                          onPlaceSelect={(place) => setLocationData(place)}
                          value={locationData?.address || ''}
                          required
                        />
                        {locationData && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <i className="fas fa-check-circle text-blue-600 mt-0.5"></i>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-blue-900 mb-1">Location selected</p>
                                <p className="text-sm text-blue-700">{locationData.address}</p>
                                {locationData.name && locationData.name !== locationData.address && (
                                  <p className="text-xs text-blue-600 mt-1">Name: {locationData.name}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => setLocationData(null)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <ManualVenueForm value={manualVenue} onChange={setManualVenue} />
                    )}
                  </div>
                )}

                {formData.locationType === 'online' && (
                  <div className="relative">
                    <label 
                      className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                    >
                      Event URL *
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <div className="relative">
                        <i className="fas fa-link absolute left-4 top-4 text-gray-400 pointer-events-none"></i>
                        <input
                          type="url"
                          value={formData.onlineUrl || ''}
                          onChange={(e) => setFormData({ ...formData, onlineUrl: e.target.value })}
                          placeholder="https://..."
                          className="w-full py-3 pl-12 pr-4 border-0 rounded-lg focus:outline-none transition-all"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {formData.locationType === 'tba' && (
                  <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    Location will be announced later.
                  </div>
                )}
              </div>

              {/* Category */}
              <div className="space-y-4">
                <SearchableCategorySelect
                  categories={categories}
                  value={formData.categoryIds?.length ? formData.categoryIds : (formData.categoryId ? [formData.categoryId] : [])}
                  onChange={(categoryIds) => setFormData({
                    ...formData,
                    categoryIds,
                    categoryId: categoryIds[0] || ''
                  })}
                  required
                  multiple
                />
              </div>

              {/* Pricing Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Ticket pricing</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Use a single price or add multiple packages (e.g. Adult, Kids, Group).
                  </p>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usePackages}
                    onChange={(e) => setUsePackages(e.target.checked)}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-800">This event has multiple ticket packages</span>
                </label>

                {usePackages ? (
                  <PricingTierEditor tiers={pricingTiers} onChange={setPricingTiers} />
                ) : (
                <>
                {/* Price Input */}
                <div className="relative">
                  <label 
                    className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                  >
                    Price *
                  </label>
                  <div className={`border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all ${
                    isFree ? 'bg-gray-100' : ''
                  }`}>
                    <div className="relative">
                      <span className="absolute left-4 top-4 text-gray-500 font-medium">KES</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={isFree ? '0' : formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        disabled={isFree}
                        className={`w-full py-3 pl-16 pr-4 border-0 rounded-lg focus:outline-none transition-all ${
                          isFree ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        required={!isFree}
                      />
                    </div>
                  </div>
                </div>

                {/* Free Toggle */}
                <div className="flex items-center gap-3 py-2">
                  <div className="w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0 bg-white">
                    <i className={`fas fa-times text-xs ${isFree ? 'text-gray-600' : 'text-gray-300'}`}></i>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isFree}
                        onChange={(e) => {
                          const free = e.target.checked;
                          setIsFree(free);
                          if (free) {
                            setFormData({ ...formData, price: '0', earlyBirdPrice: '' });
                          }
                        }}
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                        isFree ? 'bg-blue-600' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                          isFree ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}></div>
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">My tickets are free</span>
                  </label>
                </div>

                {/* Additional Pricing Fields */}
                {!isFree && (
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="relative">
                      <label 
                        className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                      >
                        Early Bird Price (KES)
                      </label>
                      <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                        <div className="relative">
                          <span className="absolute left-4 top-4 text-gray-500 font-medium">KES</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.earlyBirdPrice}
                            onChange={(e) => setFormData({ ...formData, earlyBirdPrice: e.target.value })}
                            className="w-full py-3 pl-16 pr-4 border-0 rounded-lg focus:outline-none transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="relative">
                      <label 
                        className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                      >
                        Max Tickets *
                      </label>
                      <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                        <input
                          type="number"
                          min="1"
                          value={formData.maxTickets}
                          onChange={(e) => setFormData({ ...formData, maxTickets: e.target.value })}
                          className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none transition-all"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Max Tickets for Free Events */}
                {isFree && !usePackages && (
                  <div className="relative pt-2">
                    <label 
                      className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                    >
                      Max Tickets *
                    </label>
                    <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                      <input
                        type="number"
                        min="1"
                        value={formData.maxTickets}
                        onChange={(e) => setFormData({ ...formData, maxTickets: e.target.value })}
                        className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none transition-all"
                        required
                      />
                    </div>
                  </div>
                )}
                </>
                )}
              </div>

              {/* Additional Options */}
              <div className="space-y-4">
                <div className="relative">
                  <label 
                    className="absolute left-3 -top-2.5 text-xs text-gray-500 bg-white px-1 z-10 pointer-events-none"
                  >
                    Refund Policy
                  </label>
                  <div className="border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition-all">
                    <textarea
                      value={formData.refundPolicy}
                      onChange={(e) => setFormData({ ...formData, refundPolicy: e.target.value })}
                      rows="6"
                      className="w-full py-3 px-4 border-0 rounded-lg focus:outline-none resize-y transition-all text-sm leading-relaxed"
                    />
                  </div>
                </div>

                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                  />
                  <label className="ml-3 text-sm text-gray-700 cursor-pointer">
                    Feature this event on the homepage
                  </label>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-8 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/admin/events')}
                  className="px-8 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingImage}
                  className="px-10 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 transition-all shadow-sm hover:shadow-md"
                >
                  {loading || uploadingImage ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreateEventPage;


